import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  Package,
  Truck,
  Plus,
} from "lucide-react";
import { formatCurrency } from "@/lib/billingUtils";
import type { SupplierOrder } from "@/types/supplierOrders";

interface ConsolidationAlertProps {
  supplierId?: string;
  supplierName?: string;
  currentOrderId?: string;
  onConsolidate?: (orders: SupplierOrder[]) => void;
  onAddMaterial?: (material: any) => void;
}

export function ConsolidationAlert({
  supplierId,
  supplierName,
  currentOrderId,
  onConsolidate,
  onAddMaterial,
}: ConsolidationAlertProps) {
  // Fetch pending orders for this supplier
  const { data: pendingOrders = [] } = useQuery<SupplierOrder[]>({
    queryKey: ["pending-orders-supplier", supplierId],
    queryFn: async () => {
      if (!supplierId) return [];
      let query = api
        .from("supplier_orders")
        .select("id, order_number, order_date, total_amount, status")
        .eq("supplier_id", supplierId)
        .in("status", ["draft", "pending"]);

      if (currentOrderId) {
        query = query.neq("id", currentOrderId);
      }

      const { data, error } = await query.order("order_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!supplierId,
  });

  // Fetch low stock materials from this supplier using billing_customer_id directly
  const { data: lowStockMaterials = [] } = useQuery({
    queryKey: ["low-stock-supplier", supplierId],
    queryFn: async () => {
      if (!supplierId) return [];
      const { data, error } = await api
        .from("raw_materials")
        .select("id, code, name, quantity, min_stock, unit")
        .eq("billing_customer_id", supplierId)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      // Filter materials where quantity < min_stock
      return (data || []).filter((m: any) => 
        m.min_stock && m.quantity < m.min_stock
      );
    },
    enabled: !!supplierId,
  });

  if (pendingOrders.length === 0 && lowStockMaterials.length === 0) {
    return null;
  }

  const totalPending = pendingOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);

  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardContent className="pt-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <h4 className="font-medium text-amber-800">
                Oportunidad de consolidacion
              </h4>
              <p className="text-sm text-amber-700">
                Aprovecha este pedido para reponer materiales con stock bajo
              </p>
            </div>

            {/* Pending Orders */}
            {pendingOrders.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                  <Truck className="w-4 h-4" />
                  {pendingOrders.length} pedido{pendingOrders.length > 1 ? "s" : ""} pendiente{pendingOrders.length > 1 ? "s" : ""}
                  <span className="text-amber-600">({formatCurrency(totalPending)})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {pendingOrders.slice(0, 3).map((order) => (
                    <Badge
                      key={order.id}
                      variant="outline"
                      className="bg-white border-amber-300"
                    >
                      {order.order_number} - {formatCurrency(order.total_amount)}
                    </Badge>
                  ))}
                  {pendingOrders.length > 3 && (
                    <Badge variant="outline" className="bg-white border-amber-300">
                      +{pendingOrders.length - 3} mas
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Low Stock Materials */}
            {lowStockMaterials.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                  <Package className="w-4 h-4" />
                  {lowStockMaterials.length} material{lowStockMaterials.length > 1 ? "es" : ""} con stock bajo
                </div>
                <div className="flex flex-wrap gap-2">
                  {lowStockMaterials.slice(0, 5).map((mat: any) => (
                    <Badge
                      key={mat.id}
                      variant="outline"
                      className="bg-white border-amber-300 cursor-pointer hover:bg-amber-100 flex items-center gap-1"
                      onClick={() => onAddMaterial?.(mat)}
                    >
                      {mat.name}
                      <span className="text-xs text-red-600">
                        ({mat.quantity}/{mat.min_stock} {mat.unit})
                      </span>
                      {onAddMaterial && <Plus className="w-3 h-3 ml-1" />}
                    </Badge>
                  ))}
                  {lowStockMaterials.length > 5 && (
                    <Badge variant="outline" className="bg-white border-amber-300">
                      +{lowStockMaterials.length - 5} mas
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Action */}
            {onConsolidate && pendingOrders.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="bg-white"
                onClick={() => onConsolidate(pendingOrders)}
              >
                Consolidar pedidos
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
